import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  TextFieldProps,
  TextFieldVariants
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useState } from "react";
import IconCircleWrapper from "../IconCircleWrapper";
type FormFieldType = TextFieldProps["type"] | "otp";

export interface FormFieldProps
  extends Omit<
    {
      /**
       * The variant to use.
       * @default 'outlined'
       */
      variant?: TextFieldVariants;
    } & Omit<TextFieldProps, "variant">,
    "type"
  > {
  labelExtra?: React.ReactNode;
  endAdornment2?: React.ReactNode;
  type?: FormFieldType;
}

const PasswordField: React.FC<TextFieldProps> = (props) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <TextField
      {...props}
      type={showPassword ? "text" : "password"}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export const FormField = React.forwardRef<HTMLInputElement, FormFieldProps>(
  ({ labelExtra, label, error: hasError, type = "text", variant = "outlined", ...props }, ref) => {
    const theme = useTheme();
    const baseInputRef = props.inputRef || ref;

    // On iOS, autoFocus doesn't work after navigation. Use a callback ref
    // to focus the input as soon as the DOM node is attached. If a gesture
    // handler primed iOS with a dummy input (see primeIOSFocus), this
    // focus() call hands the keyboard over and then drops the primer.
    const autoFocusCallbackRef = useCallback((node: HTMLInputElement | null) => {
      if (node && props.autoFocus) {
        requestAnimationFrame(() => {
          node.focus({ preventScroll: true });
          document.getElementById('__ios_focus_primer__')?.remove();
        });
      }
      if (typeof baseInputRef === 'function') {
        baseInputRef(node);
      } else if (baseInputRef && typeof baseInputRef === 'object') {
        (baseInputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const inputRefToUse = props.autoFocus ? autoFocusCallbackRef : baseInputRef;

    const renderField = () => {
      if (variant === "standard") {
        const { slotProps = {}, inputRef: _inputRef, ...rest } = props;
        const { input = {}, ...restSlotProps } = slotProps as any;
        const { startAdornment, ...restInput } = input;

        const commonProps = {
          ...rest,
          value: props.value,
          onChange: props.onChange,
          error: hasError,
          inputRef: inputRefToUse,
          variant,
          sx: {
            mt: 0.5,
            ...props.sx
          },
          slotProps: {
            ...restSlotProps,
            input: {...restInput, inputRef: inputRefToUse, startAdornment: props.endAdornment2 || undefined},
          },
        };


        return (
          <Stack useFlexGap direction="row" alignItems="flex-start" spacing={2}>
            {startAdornment && (
              <IconCircleWrapper
                size={32}
                bgcolor={theme.palette.navy[300]}
                sx={{
                  mt: rest.multiline === true ? 0 : 2
                }}
              >
                {startAdornment}
              </IconCircleWrapper>)
            }
            <TextField {...commonProps} type={type} label={label} />
          </Stack>
        );
      }

      const { inputRef: _inputRef2, ...restProps } = props;
      const commonProps = {
        ...restProps,
        error: hasError,
        inputRef: inputRefToUse,
        variant,
        sx: {
          mt: 0.5,
          ...props.sx
        },
      };

      return type === "password" ? (
        <PasswordField {...commonProps} />
      ) : (
        <TextField {...commonProps} type={type} />
      );
    };

    if (!label || variant === "standard") return renderField();

    return (
      <FormControl
        fullWidth={props.fullWidth}
        color={hasError ? "error" : "primary"}
      >
        <Stack direction="row" justifyContent="space-between" width="100%">
          <FormLabel
            required={props.required}
            color={hasError ? "error" : "primary"}
            sx={{ flex: 1 }}
          >
            {label}
          </FormLabel>
          {labelExtra}
        </Stack>
        {renderField()}
      </FormControl>
    );
  }
);

FormField.displayName = "FormField";